// src/pages/finance/payments/PaymentsPage.tsx
import React, { useState, useEffect } from "react";
import { Typography, Button, Modal, Descriptions, Divider } from "antd";
import { UndoOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import { getSessionUser } from "../../../utils/auth/sessionUser";

// Layout
import Layout from "../../../components/Layout";
import PageContainer from "../../../components/shared/PageContainer";
import { DataTable, StatusTag, PageToolbar, DetailDrawer, InlineLoading, EmptyState } from "../../../components/shared";

// API
import { fetchPayments, fetchPayment, refundPayment, fetchTransactionsByPayment } from "../../../api/finance/FinanceApi";
import { fetchIntegrations } from "../../../api/admin/integrationApi";

// Types
import { Payment, PaymentStatus, Transaction } from "../../../types/finance/FinanceTypes";

// Hooks
import { useNotifications } from "../../../context/NotificationContext";
import { loadUserPreferences } from "../../../utils/userPrefs/UserPrefs";
import { fetchClinicCompany } from "../../../api/admin/companyApi";
import { formatStripeAmount } from "../../../utils/stripe/stripeConfig";

const { Text } = Typography;
const { confirm } = Modal;

const PaymentsPage: React.FC = () => {
	const [payments, setPayments] = useState<Payment[]>([]);
	const [totalPayments, setTotalPayments] = useState<number>(0);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
	const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
	const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
	const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
	const [activeDrawerTab, setActiveDrawerTab] = useState<string>("info");
	const [transactions, setTransactions] = useState<Transaction[]>([]);
	const [loadingTransactions, setLoadingTransactions] = useState<boolean>(false);
	const [stripePaymentIntent, setStripePaymentIntent] = useState<any>(null);
	const [stripeEnabled, setStripeEnabled] = useState<boolean>(false);

	const PAYMENTS_PER_PAGE = loadUserPreferences().tableRowsPerPage;
	const { showError, showSuccess } = useNotifications();

	const user = getSessionUser();
	const [companyId, setCompanyId] = useState<string>("");

	useEffect(() => {
		initialize();
	}, [currentPage, statusFilter, typeFilter]);

	const initialize = async () => {
		setIsLoading(true);
		try {
			const company = await fetchClinicCompany();
			setCompanyId(company.id.toString());
			const integrations = await fetchIntegrations(company.id);
			const stripeIntegration = integrations.find((i) => i.integration_type === "stripe" && i.is_active);
			setStripeEnabled(!!stripeIntegration);
			await loadPayments(company.id.toString());
		} catch (error) {
			showError("Failed to initialize payments page");
		} finally {
			setIsLoading(false);
		}
	};

	const loadPayments = async (compId: string) => {
		try {
			const { data, total } = await fetchPayments(currentPage, PAYMENTS_PER_PAGE);
			let filteredData = data;
			if (statusFilter) filteredData = filteredData.filter((p) => p.status === statusFilter);
			if (typeFilter) filteredData = filteredData.filter((p) => p.type === typeFilter);
			setPayments(filteredData);
			setTotalPayments(total);
		} catch (error) {
			showError("Failed to load payments");
		}
	};

	const handleViewPayment = async (payment: Payment) => {
		setSelectedPayment(payment);
		setDrawerVisible(true);
		setActiveDrawerTab("info");
		setLoadingTransactions(true);
		setStripePaymentIntent(null);
		try {
			const txns = await fetchTransactionsByPayment(payment.id);
			setTransactions(txns);
		} catch (error) {
			// Non-critical — transactions are supplementary
		} finally {
			setLoadingTransactions(false);
		}
	};

	const handleRefund = (payment: Payment) => {
		confirm({
			title: "Confirm Refund",
			content: `Are you sure you want to refund ${formatCurrency(payment.amount)}?`,
			okText: "Refund",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await refundPayment(payment.id, {});
					showSuccess("Payment refunded successfully");
					await loadPayments(companyId);
					if (selectedPayment?.id === payment.id) {
						const updated = await fetchPayment(payment.id);
						setSelectedPayment(updated);
					}
				} catch (error) {
					showError("Failed to refund payment");
				}
			},
		});
	};

	const handleReset = () => {
		setSearchTerm("");
		setStatusFilter(undefined);
		setTypeFilter(undefined);
	};

	// ⚠️ `amount` arrives as a dollar STRING (`"25.99"`) — the finance service's dollar-named
	// columns are Postgres `numeric` GENERATED columns and TypeORM does not coerce those to a
	// number. `FinanceTypes.Payment.amount` was corrected to `MoneyString` in I8; this signature
	// follows it. Behaviour is unchanged — `Intl.NumberFormat.format` already coerced the string
	// at runtime, so this only makes the types agree with what was always happening.
	const formatCurrency = (amount: number | string): string =>
		new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(amount));

	// ── Columns ──────────────────────────────────────────────────
	const columns = [
		{
			title: "Date",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 140,
			render: (date: string) => format(new Date(date), "dd MMM yyyy HH:mm"),
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			width: 120,
		},
		{
			title: "Amount",
			dataIndex: "amount",
			key: "amount",
			width: 140,
			render: (amount: number, record: Payment) => (
				<Text strong style={{ color: "var(--mg-success)" }}>
					{formatCurrency(amount)} {record.currency}
				</Text>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 110,
			render: (status: PaymentStatus) => <StatusTag status={status} />,
		},
		{
			title: "Method",
			key: "paymentMethod",
			width: 110,
			render: (_: any, record: Payment) =>
				record.metadata?.stripe_payment_intent_id ? (
					<StatusTag status="processing" label="Stripe" />
				) : (
					<Text type="secondary" style={{ fontSize: 13 }}>Standard</Text>
				),
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			ellipsis: true,
		},
		{
			title: "",
			key: "actions",
			width: 56,
			render: (_: any, record: Payment) =>
				record.status === PaymentStatus.COMPLETED ? (
					<Button
						type="text"
						size="small"
						danger
						icon={<UndoOutlined />}
						onClick={(e) => { e.stopPropagation(); handleRefund(record); }}
					/>
				) : null,
		},
	];

	const transactionColumns = [
		{
			title: "Date",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 160,
			render: (date: string) => format(new Date(date), "dd MMM yyyy HH:mm"),
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			width: 120,
		},
		{
			title: "Amount",
			dataIndex: "amount",
			key: "amount",
			width: 140,
			render: (amount: number, record: Transaction) => (
				<Text strong>
					{formatCurrency(amount)} {record.currency}
				</Text>
			),
		},
		{
			title: "Transaction ID",
			dataIndex: "transactionId",
			key: "transactionId",
			ellipsis: true,
		},
	];

	// ── Drawer tabs ───────────────────────────────────────────────
	const drawerTabs = selectedPayment
		? [
				{
					key: "info",
					label: "Payment Info",
					children: (
						<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
							<div>
								<Descriptions bordered size="small" column={1}>
									<Descriptions.Item label="Payment ID">{selectedPayment.id}</Descriptions.Item>
									<Descriptions.Item label="Amount">
										<Text strong style={{ color: "var(--mg-success)", fontSize: 16 }}>
											{formatCurrency(selectedPayment.amount)} {selectedPayment.currency}
										</Text>
									</Descriptions.Item>
									<Descriptions.Item label="Status">
										<StatusTag status={selectedPayment.status} />
									</Descriptions.Item>
									<Descriptions.Item label="Type">{selectedPayment.type}</Descriptions.Item>
									<Descriptions.Item label="Description">{selectedPayment.description || "—"}</Descriptions.Item>
									<Descriptions.Item label="Created">{format(new Date(selectedPayment.createdAt), "dd MMM yyyy HH:mm:ss")}</Descriptions.Item>
									<Descriptions.Item label="Updated">{format(new Date(selectedPayment.updatedAt), "dd MMM yyyy HH:mm:ss")}</Descriptions.Item>
									{selectedPayment.failureReason && (
										<Descriptions.Item label="Failure Reason">
											<Text type="danger">{selectedPayment.failureReason}</Text>
										</Descriptions.Item>
									)}
								</Descriptions>
							</div>

							<Divider style={{ margin: "4px 0" }} />
							<Text strong style={{ fontSize: 13 }}>Transaction History</Text>

							{loadingTransactions ? (
								<InlineLoading minHeight={120} message="Loading transactions…" />
							) : transactions.length > 0 ? (
								<DataTable
									bare
									columns={transactionColumns}
									dataSource={transactions}
									rowKey="id"
									loading={false}
									pagination={false}
									emptyTitle="No transactions found"
								/>
							) : (
								<EmptyState title="No transactions found" description="No transaction history for this payment" />
							)}
						</div>
					),
				},
				...(stripeEnabled && selectedPayment.metadata?.stripe_payment_intent_id
					? [
							{
								key: "stripe",
								label: "Stripe Details",
								children: (
									<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
										<Descriptions bordered size="small" column={1}>
											<Descriptions.Item label="Payment Intent ID">
												{selectedPayment.metadata.stripe_payment_intent_id}
											</Descriptions.Item>
											<Descriptions.Item label="Client Secret">
												{selectedPayment.metadata.stripe_client_secret ? "••••••••" : "—"}
											</Descriptions.Item>
											{selectedPayment.metadata.stripe_customer_id && (
												<Descriptions.Item label="Customer ID">
													{selectedPayment.metadata.stripe_customer_id}
												</Descriptions.Item>
											)}
											{selectedPayment.metadata.stripe_charge_id && (
												<Descriptions.Item label="Charge ID">
													{selectedPayment.metadata.stripe_charge_id}
												</Descriptions.Item>
											)}
											{stripePaymentIntent && (
												<>
													<Descriptions.Item label="Stripe Status">
														<StatusTag status={stripePaymentIntent.status} />
													</Descriptions.Item>
													{stripePaymentIntent.amount && (
														<Descriptions.Item label="Stripe Amount">
															{formatStripeAmount(stripePaymentIntent.amount, stripePaymentIntent.currency?.toUpperCase())}
														</Descriptions.Item>
													)}
												</>
											)}
										</Descriptions>

										{stripePaymentIntent?.payment_method?.card && (
											<>
												<Divider style={{ margin: "4px 0" }} />
												<Text strong style={{ fontSize: 13 }}>Payment Method</Text>
												<Descriptions bordered size="small" column={1}>
													<Descriptions.Item label="Type">{stripePaymentIntent.payment_method.type}</Descriptions.Item>
													<Descriptions.Item label="Card Brand">{stripePaymentIntent.payment_method.card.brand}</Descriptions.Item>
													<Descriptions.Item label="Last 4">{stripePaymentIntent.payment_method.card.last4}</Descriptions.Item>
													<Descriptions.Item label="Expiry">
														{stripePaymentIntent.payment_method.card.exp_month}/{stripePaymentIntent.payment_method.card.exp_year}
													</Descriptions.Item>
												</Descriptions>
											</>
										)}

										{!stripePaymentIntent && (
											<EmptyState title="Stripe details not available" description="Payment intent details could not be loaded" />
										)}
									</div>
								),
							},
					  ]
					: []),
		  ]
		: [];

	return (
		<Layout>
			<PageContainer
				data-testid="payments-root"
			>
				<PageToolbar
					filters={[
						{
							type: "search",
							placeholder: "Search payments…",
							value: searchTerm,
							onChange: setSearchTerm,
							width: 220,
						},
						{
							type: "select",
							placeholder: "Filter by status",
							options: [
								{ label: "Pending", value: PaymentStatus.PENDING },
								{ label: "Processing", value: PaymentStatus.PROCESSING },
								{ label: "Completed", value: PaymentStatus.COMPLETED },
								{ label: "Failed", value: PaymentStatus.FAILED },
								{ label: "Refunded", value: PaymentStatus.REFUNDED },
								{ label: "Cancelled", value: PaymentStatus.CANCELLED },
							],
							value: statusFilter,
							onChange: setStatusFilter,
							allowClear: true,
							width: 160,
						},
						{
							type: "select",
							placeholder: "Filter by type",
							options: [
								{ label: "Subscription", value: "subscription" },
								{ label: "Booking", value: "booking" },
								{ label: "Ecommerce", value: "ecommerce" },
								{ label: "Invoice", value: "invoice" },
							],
							value: typeFilter,
							onChange: setTypeFilter,
							allowClear: true,
							width: 160,
						},
					]}
					onReset={handleReset}
				/>

				<DataTable
					columns={columns}
					dataSource={payments}
					rowKey="id"
					loading={isLoading}
					total={totalPayments}
					currentPage={currentPage}
					pageSize={PAYMENTS_PER_PAGE}
					onPageChange={(p) => setCurrentPage(p)}
					onRowClick={handleViewPayment}
					emptyTitle="No payments found"
					emptyDescription="Payments will appear here once transactions are processed"
				/>

				{/* Payment Details Drawer */}
				<DetailDrawer
					open={drawerVisible}
					onClose={() => { setDrawerVisible(false); setSelectedPayment(null); }}
					width="70vw"
					title={selectedPayment ? `Payment — ${selectedPayment.id}` : "Payment Details"}
					subtitle={
						selectedPayment
							? `${formatCurrency(selectedPayment.amount)} ${selectedPayment.currency}`
							: undefined
					}
					headerActions={
						selectedPayment?.status === PaymentStatus.COMPLETED ? (
							<Button data-testid="payments-refund-btn" danger icon={<UndoOutlined />} onClick={() => handleRefund(selectedPayment)}>
								Refund Payment
							</Button>
						) : undefined
					}
					tabs={drawerTabs}
					activeTab={activeDrawerTab}
					onTabChange={setActiveDrawerTab}
				/>
			</PageContainer>
		</Layout>
	);
};

export default PaymentsPage;
